import type { ExtendedTier } from '@/types/ExtendedTier';

type ValidationMode = 'add' | 'edit';
export const MAX_EXTENDED_TIER_RATE = 99999;

export interface ValidateExtendedTierCandidateInput {
  tiers: ExtendedTier[];
  highestOvertimeRate: number;
  name: string;
  rate: number | null;
  mode: ValidationMode;
  editIndex?: number | null;
}

export function sortExtendedTiers(tiers: ExtendedTier[]): ExtendedTier[] {
  return [...tiers].sort((a, b) => a.sortOrder - b.sortOrder);
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

function normalizeRate(rate: number): number {
  // Avoid floating-point equality surprises in duplicate checks.
  return Number(rate.toFixed(6));
}

export function normalizeExtendedTiers(
  tiers: ExtendedTier[],
  highestOvertimeRate: number,
): ExtendedTier[] {
  const sorted = sortExtendedTiers(tiers);
  const next: ExtendedTier[] = [];
  const seenNames = new Set<string>();
  const seenRates = new Set<number>();

  for (const tier of sorted) {
    const name = (tier.name ?? '').trim();
    const rate = Number(tier.rate);
    if (!name) continue;
    if (!Number.isFinite(rate)) continue;
    if (rate <= highestOvertimeRate) continue;

    const nameKey = normalizeName(name);
    const rateKey = normalizeRate(rate);
    if (seenNames.has(nameKey) || seenRates.has(rateKey)) continue;

    seenNames.add(nameKey);
    seenRates.add(rateKey);
    next.push({
      name,
      rate,
      sortOrder: next.length + 1,
    });
  }

  return next;
}

export function validateExtendedTierCandidate(input: ValidateExtendedTierCandidateInput): string | null {
  const { tiers, highestOvertimeRate, name, rate, mode, editIndex } = input;
  const trimmedName = (name ?? '').trim();

  if (!trimmedName) return 'Extended tier name is required';
  if (rate == null || !Number.isFinite(rate)) return 'Rate is required';
  if (rate > MAX_EXTENDED_TIER_RATE) return `Rate must be at most ${MAX_EXTENDED_TIER_RATE}x`;
  if (rate <= highestOvertimeRate) return `Extended tier rate must be greater than ${highestOvertimeRate}x`;

  const ordered = sortExtendedTiers(tiers);
  const candidateIndex = mode === 'add' ? ordered.length : (editIndex ?? -1);
  if (candidateIndex < 0 || candidateIndex >= (mode === 'add' ? ordered.length + 1 : ordered.length)) {
    return 'Unable to save tier';
  }

  const nameKey = normalizeName(trimmedName);
  const rateKey = normalizeRate(rate);

  for (let i = 0; i < ordered.length; i++) {
    if (mode === 'edit' && i === candidateIndex) continue;
    const existing = ordered[i];
    if (normalizeName(existing.name) === nameKey) return 'Name must be unique';
    if (normalizeRate(Number(existing.rate)) === rateKey) return 'Rate must be unique';
  }

  return null;
}
